import { Logger } from 'typescript-log'
import { GptApi, SlotRenderEndedEvent } from '@news-mono/web-common'
import { GptAdSlotRegistration } from './gpt-ad-provider'
import { GptAdSlotDefinition } from '@news-mono/web-common'
import { isServerEnvironment } from '@news-mono/web-common'

// Logs when cleanupAndDisplaySlots is queued, and runs.
// It is pretty noisy and only really needs to be logged if debugging
// everything is working properly
const logDiffTrigger = false

/**
 * This class tracks registered ad slots, and ensures that ads are created and destroyed at the correct time.
 *
 * It is basically the bridge between the declaritive world of React and the imperitive world of the GPT library
 */
export class GptSlotManager {
    private renderedAdSlots: GptAdSlotRegistration[] = []
    private definedSlots: {
        [renderKey: string]: GptAdSlotDefinition[]
    } = {}
    private queuedCleanupAndDisplaySlotsWhenIdleCall: undefined | any
    private renderKeyChanging: Promise<void> | undefined
    private currentRenderKey: string | undefined

    constructor(
        private gptApi: GptApi,
        private log: Logger,
        private shouldDisplaySlot: (slot: GptAdSlotRegistration) => boolean,
        renderKey: string | null,

        private options?: {
            requestIdleCallback?: typeof window.requestIdleCallback
        },
    ) {
        if (!isServerEnvironment()) {
            if (renderKey) {
                this.renderKeyChanged(renderKey)
            }
            gptApi.registerOnSlotRenderEnded(this.onSlotRenderEnded.bind(this))
        }
    }

    private onSlotRenderEnded(event: SlotRenderEndedEvent) {
        const slotId = event.slot.getSlotElementId()
        this.renderedAdSlots
            .filter((slot) => slot.id === slotId)
            .forEach((slot) => slot.onSlotRenderEnded(event))
    }

    defineSlots(renderKey: string, slots: GptAdSlotDefinition[]) {
        this.log.debug(
            { renderKey, slots: slots.map((slot) => slot.id) },
            'GPT Slot Manager: Slots defined',
        )
        this.definedSlots[renderKey] = slots
        this.cleanupAndDisplaySlotsWhenIdle('Slots defined')
    }

    registerSlot(slot: GptAdSlotRegistration) {
        if (!this.currentRenderKey) {
            this.log.error(
                { slotId: slot.id },
                'GPT Slot Manager: Slot rendered without currentRenderKey',
            )
            return
        }
        this.log.debug({ slotId: slot.id }, 'GPT Slot Manager: Slot rendered')

        const registered = this.renderedAdSlots.filter(
            (currentSlot) => currentSlot.id === slot.id,
        )

        if (registered.length) {
            this.log.error(
                { registered, slotId: slot.id },
                'GPT Slot Manager: GPT slot with the same ID registered multiple times!',
            )
            return
        }

        this.renderedAdSlots.push(slot)

        this.cleanupAndDisplaySlotsWhenIdle('slot registered')
    }

    unregisterSlot(slotId: string) {
        this.log.debug({ slotId }, 'GPT Slot Manager: Slot unmounted')
        const slotIndex = this.renderedAdSlots.findIndex(
            (slotRefference) => slotRefference.id === slotId,
        )

        if (slotIndex !== -1) {
            this.renderedAdSlots.splice(slotIndex, 1)

            const slotDef = this.getSlotDefinition(slotId)
            const displayedSlot = this.gptApi.displayedSlots[slotId]

            if (slotDef && displayedSlot) {
                this.gptApi.destroySlots([slotDef])
            }
        }
    }

    /** Called once per page */
    renderKeyChanged(renderKey: string) {
        const currentRenderKey = this.currentRenderKey

        if (!currentRenderKey) {
            this.currentRenderKey = renderKey
            this.log.debug(
                { currentRenderKey: this.currentRenderKey },
                'GPT Slot Manager: Initial Render key set',
            )
        } else {
            this.log.debug(
                { renderKey, currentRenderKey },
                'GPT Slot Manager: Render key changed',
            )
            this.gptApi.destroySlots(this.definedSlots[currentRenderKey])
            this.currentRenderKey = renderKey
        }

        const isDifferentRender = () => this.currentRenderKey !== renderKey

        this.renderKeyChanging = this.gptApi
            .setPageAdditionalTargeting(isDifferentRender)
            .then(() => {
                if (isDifferentRender()) {
                    return
                }

                this.log.debug(
                    { renderKey, currentRenderKey },
                    'GPT Slot Manager: Page targeting set, displaying slots',
                )

                // Clear as part of this microtask, then cleanupAndDisplaySlots will run
                this.renderKeyChanging = undefined
                this.cleanupAndDisplaySlotsWhenIdle('render key changed', true)
            })
    }

    /**
     * This function is called multiple times during the page lifecycle
     **/
    cleanupAndDisplaySlotsWhenIdle(reason: string, updateCorrelator = false) {
        if (
            this.queuedCleanupAndDisplaySlotsWhenIdleCall ||
            this.renderKeyChanging
        ) {
            return
        }

        logDiffTrigger &&
            this.log.debug(
                { reason, updateCorrelator },
                'GPT Slot Manager: Queued cleanupAndDisplaySlots when idle',
            )

        const ric =
            this.options?.requestIdleCallback || window.requestIdleCallback
        this.queuedCleanupAndDisplaySlotsWhenIdleCall = ric(
            () => this.cleanupAndDisplaySlots(updateCorrelator),
            { timeout: 100 },
        )
    }

    private cleanupAndDisplaySlots(updateCorrelator: boolean) {
        this.queuedCleanupAndDisplaySlotsWhenIdleCall = undefined
        // Some browsers do not support cancelling requestIdleCallback, so we need
        // to not run this function if we are setting up a page, this will be run once
        // it completes
        if (this.renderKeyChanging) {
            return
        }

        logDiffTrigger &&
            this.log.debug(
                { updateCorrelator },
                'GPT Slot Manager: cleanupAndDisplaySlots',
            )
        this.cleanUpSlots()
        this.displayViewableSlots(updateCorrelator)
    }

    private displayViewableSlots(updateCorrelator: boolean) {
        const slotsToLoad: GptAdSlotDefinition[] = this.renderedAdSlots
            .filter((slot) => {
                const shouldDisplay = this.shouldDisplaySlot(slot)
                return shouldDisplay
            })
            .map((slot) => this.getSlotDefinition(slot.id))
            .filter((slot): slot is GptAdSlotDefinition => slot !== undefined)

        if (slotsToLoad.length === 0) {
            return
        }

        this.gptApi.displaySlots(slotsToLoad, updateCorrelator)
    }

    private cleanUpSlots() {
        const displayedSlots = Object.keys(this.gptApi.displayedSlots)
        const missingSlots = displayedSlots.filter(
            (displayedSlot) =>
                !this.definedSlots[this.currentRenderKey!].some(
                    (slot) => displayedSlot === slot.id,
                ),
        )
        const slotsWithMissingConfiguration = displayedSlots.filter(
            (displayedSlot) =>
                !this.definedSlots[this.currentRenderKey!].some(
                    (slot) => displayedSlot === slot.id,
                ),
        )
        const slotsToCleanup = missingSlots.concat(
            slotsWithMissingConfiguration,
        )
        if (slotsToCleanup.length) {
            this.gptApi.destroySlots(
                slotsToCleanup.map(
                    (missingSlot) =>
                        this.gptApi.displayedSlots[missingSlot].definition,
                ),
            )
        }
    }

    getSlotDefinition = (slotId: string) => {
        if (!this.currentRenderKey) {
            return
        }

        if (!this.definedSlots[this.currentRenderKey]) {
            return
        }

        return this.definedSlots[this.currentRenderKey].find(
            (slotDefinition) => slotDefinition.id === slotId,
        )
    }
}
